import "../styling/PaymentMethodsStyles.css";
import { useEffect, useState, useCallback, useMemo } from "react";
import api from "../../../api/api";
import { useCheckout } from "../../../context/CheckoutContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { formatFrequencyUnit } from "../../../utils/commonUtils";
import { toast } from "react-toastify";
import { fetchSession, updateSession } from "../../../utils/paymentSession";
import { fetchGatewayConnection, processPayment } from "../../../api/api";
import { safeRedirect } from "../../../utils/redirectUrlBuilder";
import { formatPaymentError } from "../../../utils/formatPaymentError";
import { handleCitPaymentApiResponse, applyCitDeclineOrErrorUi, CIT_OUTCOME } from "../../../utils/paymentOutcome";
import useResumePendingPayment from "../../../hooks/useResumePendingPayment";
import DlocalUpiFormView from "./DlocalUpiFormView";
import useWebSocket from "../../../hooks/useWebSocket";

const DlocalUpiForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  platformItemDetails,
  checkoutProcessDto,

  // Props for Altruon variant
  onUpiIdChange,
  onPanChange,
  isEmbeddedCheckout = false,
  onParentCallback,
  sessionId
}) => {
  const [upiId, setUpiId] = useState("");
  const [pan, setPan] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [panError, setPanError] = useState("");
  const [transactionId, setTransactionId] = useState(null);
  const { t } = useTranslation();
  const {
    triggerPaymentMethodEvent,
    resetTrigger,
    clientIp,
    clientCountry,
    paymentMethodName,
    platformName,
    estimatedOrder,
    isHostedPageMode,
  } = useCheckout();

  const upiDlocalRouting = useMemo(() => {
    return routingEntries?.find(
      (entry) => entry.paymentMethod === "upi" && entry.platform === "dlocal"
    );
  }, [routingEntries]);

  const validateUpiId = useCallback(
    (value) => {
      const upiRegex = /^[a-zA-Z0-9.\-_]{2,}@[a-zA-Z]{2,}$/;
      if (!upiRegex.test(value)) {
        setError(t("paymentForms.upi.invalidUpi"));
        return false;
      }
      return true;
    },
    [t]
  );

  const validatePan = useCallback(
    (value) => {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (platformItemDetails?.requirePan && !panRegex.test(value)) {
        setPanError(t("paymentForms.upi.invalidPan"));
        return false;
      }
      return true;
    },
    [t, platformItemDetails?.requirePan]
  );

  const handleUpiIdChange = useCallback(
    (e) => {
      const value = e.target.value;
      setUpiId(value);
      setError("");
      onUpiIdChange?.(value);
    },
    [onUpiIdChange]
  );

  const handlePanChange = useCallback(
    (e) => {
      const value = e.target.value.toUpperCase();
      setPan(value);
      setPanError("");
      onPanChange?.(value);
    },
    [onPanChange]
  );

  const buildPayload = (gatewayConnection, latestSession) => {
    const amount =
       estimatedOrder?.futureInvoiceAmountDue ? 
            Math.round(estimatedOrder.futureInvoiceAmountDue * 100) : 
            latestSession?.paymentData?.amount ?? 1000;
           
    return {
      paymentData: {
        gatewayConnectionId: gatewayConnection.id,
        gatewayName: gatewayConnection.platform,
        paymentMethod: "upi",
        // amount,
        // currency: platformItemDetails?.currencyCode || latestSession?.paymentData?.currency || "USD",
        transactionId: "",
        additionalData: {
          payment_method_flow: "REDIRECT",
          document: pan,
          wallet: {
            username: upiId,
            email: formData?.email,
            save: true,
            capture: true,
            verify: false,
            expiration: 60,
            recurring_info: {
              subscription_frequency_unit: "MONTH",
              subscription_frequency: itemDetails?.period,
            },
          },
        },
      },
      customerData: {
        firstName: formData?.firstName || latestSession?.customerData?.firstName,
        lastName: formData?.lastName || latestSession?.customerData?.lastName,
        email: formData?.email || latestSession?.customerData?.email,
        ip: clientIp || "192.0.126.111",
        billingAddress: {
          country: formData?.billingAddress?.country || latestSession?.customerData?.billingAddress?.country || clientCountry || "US",
          zipCode: formData?.billingAddress?.zipCode || latestSession?.customerData?.billingAddress?.zipCode || "90210",
          street: formData?.billingAddress?.street || latestSession?.customerData?.billingAddress?.street || "",
          city: formData?.billingAddress?.city || latestSession?.customerData?.billingAddress?.city || "",
          state: formData?.billingAddress?.state || latestSession?.customerData?.billingAddress?.state || "",
          number: "1234",
        },
      },
      billingData: {
        planId: platformItemDetails?.[0]?.id || planId,
        billingPlatformId,
        billingPlatformName: itemDetails?.billingPlatformName || latestSession?.billingData?.billingPlatformName || "",
        frequency: latestSession?.billingData?.frequency || "",
      },
    };
  };


  const handleProcessPayment = useCallback(async (payload) => {
    if (isEmbeddedCheckout) {
      onParentCallback?.({
        type: "finalizeFormData",
        data: { paymentMethod: "upi", gatewayName: "dlocal", upiId, pan },
      });
      await new Promise((r) => setTimeout(r, 800));
    }

    if (!isEmbeddedCheckout) {
      await updateSession(sessionId, payload);
    }

    return processPayment(billingPlatformId, sessionId, payload);
  }, [isEmbeddedCheckout, onParentCallback, upiId, pan, billingPlatformId, sessionId]);


  const handlePayNow = useCallback(async () => {
    if (!validateUpiId(upiId) || !validatePan(pan)) return;

    if (!upiDlocalRouting) {
      setError(t("paymentForms.upi.unavailable"));
      return;
    }

    setLoading(true);
    const citUi = {
      isHostedPageMode,
      isEmbeddedCheckout,
      t,
      setError,
      onParentCallback,
      toast,
    };

    try {
      const gateway_connection = await fetchGatewayConnection(upiDlocalRouting, t);
      const latestSession = await fetchSession(sessionId);
      // await new Promise(resolve => setTimeout(resolve, 800));
      const payload = buildPayload(gateway_connection, latestSession);
      if (!payload) {
        setLoading(false);
        return;
      }
      const res = await handleProcessPayment(payload);
      const { handled } = handleCitPaymentApiResponse(res, citUi);
      if (handled) return;

      if (res.status === 200) {
        const redirectUrl = res?.data?.redirect_url && `${res.data.redirect_url}?trxId=${res.data.newTransactionId}`;
        if (isEmbeddedCheckout) {
          setTransactionId(res.data.newTransactionId);
          if (redirectUrl) {
            onParentCallback?.({
              type: "actionRequired",
              action: "REDIRECT",
              url: redirectUrl,
            });
            safeRedirect(redirectUrl, { target: "blank" });
          } else {
            onParentCallback?.({
              type: "success",
              transactionId: res.data.newTransactionId,
            });
          }
        } else {
          // Hosted page: navigate the current tab to the UPI/APM page. dLocal
          // returns the consumer to our return URL, so the page resolves the
          // final status itself (no orphaned tab waiting on the websocket).
          if (redirectUrl) {
            toast.info(t("messages.info.redirecting"));
            safeRedirect(redirectUrl, { target: "self" });
          }
        }
      } else {
        if (isEmbeddedCheckout) {
          onParentCallback?.({ type: "failure", message: t("messages.error.somethingWrong") || "Transaction failed." });
        }
        throw new Error(t("messages.error.somethingWrong"));
      }
    } catch (e) {
      if (e?.response && handleCitPaymentApiResponse(e.response, citUi).handled) return;
      applyCitDeclineOrErrorUi(
        { outcome: CIT_OUTCOME.ERROR, message: formatPaymentError(e?.message, t) },
        citUi
      );
    } finally {
      setLoading(false);
    }
  }, [
    upiId,
    pan,
    validateUpiId,
    validatePan,
    upiDlocalRouting,
    isEmbeddedCheckout,
    handleProcessPayment,
    t,
    onParentCallback,
  ]);

  const handleResumeRedirect = useCallback(
    (redirectUrl, trxId) => {
      setTransactionId(trxId);
      toast.info(t("messages.info.redirecting"));
      safeRedirect(redirectUrl, { target: "self" });
    },
    [t]
  );

  useResumePendingPayment({
    paymentMethod: "upi",
    platform: "dlocal",
    consumed: isEmbeddedCheckout || !!transactionId,
    onResumeRedirect: handleResumeRedirect,
  });

  useEffect(() => {
    if (
      triggerPaymentMethodEvent &&
      (isEmbeddedCheckout || (paymentMethodName === "upi" && platformName === "dlocal"))) {
      handlePayNow();
      resetTrigger();
    }
  }, [
    triggerPaymentMethodEvent,
    paymentMethodName,
    platformName,
    handlePayNow,
    resetTrigger,
  ]);

   // Listen websocket only for SDK version
  useWebSocket(isEmbeddedCheckout && transactionId, (event) => {
    const data = JSON.parse(event.data); 
      
      if (!data?.status) return;

      if (data.status === "success") {
        onParentCallback?.({
          type: "success",
          transactionId,
        });
      }

      if (data.status === "failed") {
        onParentCallback?.({
          type: "failure",
        });
      }
    },
    undefined,
    undefined,
    (err) => {
      console.error("CARD WS error", err);
    }
  );

  return (
    <DlocalUpiFormView
      t={t}
      upiId={upiId}
      pan={pan}
      loading={loading}
      error={error}
      panError={panError}
      onUpiChange={handleUpiIdChange}
      onPanChange={handlePanChange}
    />
  );
};

export default DlocalUpiForm;